import { ConfigFieldValidator, ConfigTranslatedText } from '@/eventConfig/types.ts';
import { isEmpty, isNil } from 'ramda';
import { FieldValue, FormGetFieldStatusFn, FormGetFieldValueFn } from '@/form/FormProvider.tsx';
import moment from 'moment/moment';
import { parsePhoneNumber } from 'react-phone-number-input';
import { useBmiValidation } from '@/util/useBmiValidation';
import { isZaIdSkipCode, useZaIdValidator, ZaIdErrorCode } from '@/util/useZaIdValidator';
import { isRfc5322Email } from '@/util/emailValidation.ts';
import { getGenderFromSalutation } from '../../utils.tsx';

export interface ValidationResult {
  isValid: boolean;
  hint?: ConfigTranslatedText;
}

export type VFn = (validator: ConfigFieldValidator, value: FieldValue | undefined) => boolean;

// "Required" validation might reference a field and a value as a precondition
// @TODO: remove this, it seems the wrong place
type ReqReferenceType = { field: string; value: any }[];

type ValidatorFn = (
  validator: ConfigFieldValidator,
  value: FieldValue | undefined
) => { isValid: boolean; error?: ConfigTranslatedText };

export type ValidateFieldFn = (
  validators: ConfigFieldValidator[] | undefined,
  value: FieldValue | undefined
) => ValidationResult;

export interface GetIsRequiredValidationResultFn {
  (validator: ConfigFieldValidator[] | undefined, getFieldStatus: FormGetFieldStatusFn, name: string): boolean;
}

export interface FieldValidation {
  validateField: ValidateFieldFn;
  getIsRequiredValidationResult: GetIsRequiredValidationResultFn;
}

export const useFieldValidation = (getFieldValue: FormGetFieldValueFn, getFieldStatus: FormGetFieldStatusFn) => {
  const bmiValidation = useBmiValidation(getFieldValue);
  const { validateZaId } = useZaIdValidator();

  const validateRequired: VFn = (validator, value) => {
    if (!getFieldStatus) {
      return false;
    }

    if (validator.references) {
      const ref = (validator.references as unknown as ReqReferenceType)[0];
      const [_slideId, fieldName] = ref.field.split('.');
      const requiredValue = ref.value;

      const field = getFieldStatus(fieldName);

      if (field?.value.normalized === requiredValue) {
        // Now Requirement is mandatory:
        return !isEmpty(value?.raw) && !isNil(value?.raw) && !isEmpty(value?.normalized) && !isNil(value?.normalized);
      }
      // Otherwise requirement not mandatory, we return "isValid = true"
      return true;
    }

    return !isEmpty(value?.raw) && !isNil(value?.raw) && !isEmpty(value?.normalized) && !isNil(value?.normalized);
  };

  const getIsRequiredValidationResult = (
    validator: ConfigFieldValidator[] | undefined,
    getFieldStatus: FormGetFieldStatusFn,
    name: string
  ) => {
    const requiredValidator = (validator ?? []).find(({ type }) => type === 'required' || type === 'required_if');
    let isReqiredValidationResult = true;

    if (requiredValidator != undefined) {
      switch (requiredValidator.type) {
        case 'required':
          isReqiredValidationResult = validateRequired(requiredValidator, getFieldStatus(name)?.value);
          break;
        case 'required_if':
          isReqiredValidationResult = validateRequiredIf(requiredValidator, getFieldStatus(name)?.value);
          break;
      }
    }

    return isReqiredValidationResult;
  };

  const validateRegex: VFn = (validator, value) => {
    if (!value) {
      return true;
    }
    if (isEmpty(value.raw) || isNil(value.raw)) {
      return true;
    }
    if (!validator.regex) {
      return false;
    }

    const stringValue = String(value.raw);
    const regex = new RegExp(validator.regex, 'i');
    const matches = stringValue.match(regex);
    return !!matches;
  };

  const validateRfc5322Email: VFn = (_validator, value) => {
    if (!value) {
      return true;
    }

    if (isEmpty(value.raw) || isNil(value.raw)) {
      return true;
    }

    return isRfc5322Email(String(value.raw));
  };

  const validateList: VFn = (validator, value) => {
    return (validator.values ?? []).includes(value?.normalized ?? '');
  };

  const validateDate: VFn = (_validator, value) => {
    // @TODO: This seems superfluous. Remove date validation, it does nothing
    // @ts-expect-error
    const date = moment(value?.normalized);
    return date.isValid();
  };

  const validateAge: VFn = (validator, value) => {
    if (!value) {
      return true;
    }
    if (!validator.min || !validator.max) {
      return false;
    }
    // @ts-expect-error
    const age = moment().diff(value.normalized, 'years', false);
    return age >= parseInt(`${validator.min}`) && age <= parseInt(`${validator.max}`);
  };

  const validateBmi: VFn = (_validator, value) => {
    if (!getFieldStatus) {
      return false;
    }
    if (!value) {
      return false;
    }
    if (isEmpty(value.raw) || isNil(value.raw)) {
      return true;
    }
    if (bmiValidation == undefined) {
      return false;
    }

    return bmiValidation.isValidBmi;
  };

  const validateZaPassport: VFn = (validator, value) => {
    if (!getFieldStatus) return false;
    if (!value || !value.raw) return false;
    if (isEmpty(value.raw) || isNil(value.raw)) return true;

    const passportNo = String(value.normalized ?? value.raw);

    const birthdateConfirmed = getFieldStatus('birthdateConfirmed')?.value?.normalized;

    const birthdateInput = birthdateConfirmed === false ? undefined : getFieldStatus('birthdate')?.value?.normalized;

    const salutationInput = getFieldStatus('salutation')?.value?.normalized;

    const birthdate = isNil(birthdateInput) || birthdateInput === '' ? undefined : String(birthdateInput);
    const salutation = isNil(salutationInput) || salutationInput === '' ? undefined : String(salutationInput);
    const gender = salutation ? getGenderFromSalutation(salutation) : undefined;

    const errorCode = validateZaId(passportNo, birthdate, gender);

    if (!errorCode) return true;

    switch (errorCode) {
      case ZaIdErrorCode.INVALID_CHECKSUM:
        validator.error = { en: 'The ID number is not valid.' };
        break;
      case ZaIdErrorCode.BIRTHDATE_MISMATCH:
        validator.error = { en: 'The ID does not match your birthdate.' };
        break;
      case ZaIdErrorCode.GENDER_MISMATCH:
        validator.error = { en: 'The ID gender does not match your title.' };
        break;
      case ZaIdErrorCode.INVALID_FORMAT:
        validator.error = { en: 'ID must be exactly 13 digits.' };
        break;
    }

    return false;
  };

  const validateZaIdGenderMatch: VFn = (_validator, value) => {
    if (!getFieldStatus) return false;
    if (!value || isNil(value.raw) || isEmpty(value.raw)) return true;

    const salutationGender = getGenderFromSalutation(value.normalized ?? value.raw);
    if (!salutationGender) {
      return true;
    }

    const zaIdValue = getFieldStatus('za_id')?.value;
    const zaId = String(zaIdValue?.normalized ?? zaIdValue?.raw ?? '');
    if (isZaIdSkipCode(zaId)) {
      return true;
    }
    if (isEmpty(zaId) || !/^\d{13}$/.test(zaId)) {
      return true;
    }

    const genderFromZaId = parseInt(zaId[6], 10) >= 5 ? 'M' : 'F';
    return genderFromZaId === salutationGender;
  };

  const validateRequiredIf: VFn = (validator, value) => {
    if (!getFieldStatus || validator.target == undefined) {
      return false;
    }

    if (validator.target) {
      const requiredValues = validator.values;

      const field = getFieldStatus(validator.target);

      if (requiredValues?.includes(field?.value.raw ?? validator.defaultTargetValue)) {
        return validateRequired(validator, value);
      }
    }

    return true;
  };

  const validateRange: VFn = (validator, value) => {
    if (!value || value.raw === '') {
      return true;
    }
    try {
      const numberValue = parseFloat(`${value.normalized}}`);
      let valid = true;
      if (validator.min !== undefined) {
        valid = valid && numberValue >= parseFloat(`${validator.min}`);
      }

      if (validator.max !== undefined) {
        valid = valid && numberValue <= parseFloat(`${validator.max}`);
      }

      return valid;
    } catch (_e) {
      return false;
    }
  };

  const validateRUT: VFn = (_validator, value) => {
    if (!value) {
      return true;
    }
    // Remove any non-digit and non-K characters
    const rut = `${value.normalized}`.replace(/[^0-9kK]/g, '');

    // Check for valid length (should be 8-9 digits + verification digit)
    const regex = /^\d{7,8}[0-9kK]$/;
    if (!regex.test(rut)) {
      return false; // Invalid format
    }

    const digits = rut.slice(0, -1);
    const verificationDigit = rut.slice(-1).toLowerCase();

    // Calculate the expected verification digit
    let sum = 0;
    let factor = 2;

    for (let i = digits.length - 1; i >= 0; i--) {
      sum += parseInt(digits[i]) * factor;
      factor = factor === 7 ? 2 : factor + 1; // Cycle through 2-7
    }

    const remainder = sum % 11;
    const calculatedVerificationDigit = remainder === 0 ? '0' : remainder === 1 ? 'k' : (11 - remainder).toString();

    return calculatedVerificationDigit === verificationDigit; // Validate against calculated digit
  };

  const validateNotEqual: VFn = (validator, value) => {
    if (!value || !getFieldStatus) {
      return true;
    }

    const hasNoRawInput = isNil(value.raw) || (typeof value.raw === 'string' && value.raw.trim() === '');
    const hasNoNormalizedInput =
      isNil(value.normalized) || (typeof value.normalized === 'string' && value.normalized.trim() === '');

    // Skip not_equal validation when the field was only focused/tabbed but no value was entered
    if (hasNoRawInput || hasNoNormalizedInput) {
      return true;
    }

    if (!validator.target) {
      return true;
    }

    const [_slideId, fieldName] = validator.target.split('.');
    const fieldStatus = getFieldStatus(fieldName);
    const targetValue = fieldStatus?.value?.normalized;

    return targetValue !== value.normalized;
  };

  const validateEqual: VFn = (validator, value) => {
    if (!value || !getFieldStatus) {
      return true;
    }
    if (!validator.target) {
      return true;
    }

    const [_slideId, fieldName] = validator.target.split('.');
    const fieldStatus = getFieldStatus(fieldName);
    const targetValue = fieldStatus?.value?.normalized;

    if (targetValue === undefined) {
      return true;
    }

    if (targetValue === value.normalized) {
      return true;
    }

    return false;
  };

  const validatePhone: VFn = (_validator, value) => {
    // Required validation is handled in a separate validator.
    if (!value || !getFieldStatus || value.raw == undefined || value.raw == '') {
      return true;
    }

    return parsePhoneNumber(value.raw?.toString() ?? '')?.isValid() == true;
  };

  const validator: ValidatorFn = (validator, value) => {
    const appendMessage = (validatorfn: VFn) => {
      const isValid = validatorfn(validator, value);
      // Pass the message that applies to the "isValid" status
      return {
        isValid,
        error: !isValid ? validator?.error : undefined,
        // success: isValid ? validator?.success : undefined,
      };
    };

    switch (validator.type) {
      case 'required':
        return appendMessage(validateRequired);
      case 'format':
        return appendMessage(validateRegex);
      case 'list':
        return appendMessage(validateList);
      case 'date':
        return appendMessage(validateDate);
      case 'age':
        return appendMessage(validateAge);
      case 'range':
        return appendMessage(validateRange);
      case 'bmi':
        return appendMessage(validateBmi);
      case 'rut':
        return appendMessage(validateRUT);
      case 'za_id':
        return appendMessage(validateZaPassport);
      case 'za_id_gender_match':
        return appendMessage(validateZaIdGenderMatch);
      case 'not_equal':
        return appendMessage(validateNotEqual);
      case 'equal':
        return appendMessage(validateEqual);
      case 'valid_phone':
        return appendMessage(validatePhone);
      case 'required_if':
        return appendMessage(validateRequiredIf);
      case 'email_rfc5322':
        return appendMessage(validateRfc5322Email);
      default:
        console.log('Error: Validator not found. Type: ', validator.type);
        return { isValid: false };
    }
  };

  const validateField: ValidateFieldFn = (validators, value) => {
    const results = (validators || []).map((validatorDefinition) => {
      return validator(validatorDefinition, value);
    });
    const valid = results.reduce((acc, curr) => {
      return acc && curr.isValid;
    }, true);

    // Get the Messages, that match the "full" is valid status
    // and then we display the first one.
    const msg = results
      .map((result) => {
        if (!valid) {
          return result.error;
        }
      })
      .filter(Boolean);

    return {
      isValid: valid,
      hint: msg?.[0],
    };
  };

  return { validateField, getIsRequiredValidationResult };
};
